import { useFormState, useWatch } from "react-hook-form";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import { format } from "date-fns";

import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import DatePicker from "shared/components/inputs/form-fields/DatePicker";
import Options from "shared/components/inputs/form-fields/Options";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";

import useClientBizGroups, {
    useBizGroups,
} from "features/admin/clients/single/useClientBizGroups";
import {
    getCalendarDateFromNewDate,
    getDropdownOptions,
} from "features/admin/offices/OfficesForm";
import { useClientsFilterApi } from "features/admin/projects/single/useProjectsLinked";

const attributesOptions = [{ label: "Active", value: "Active" }];

const ModalCTA = ({
    isEditing,
    isLoading,
    onClose,
    handleOnClickDeleteBizGroup,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();
    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteBizGroup(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformBizGroupDataToFormData(bizGroupData = {}, clientId = "") {
    return {
        group: bizGroupData.BizGroupId ?? "",
        client: bizGroupData.ClientId ?? clientId,
        effectiveDate: bizGroupData.EffectiveDate
            ? format(new Date(bizGroupData.EffectiveDate), "yyyy-MM-dd")
            : null,
        retireDate: bizGroupData.RemovedDate
            ? format(new Date(bizGroupData.RemovedDate), "yyyy-MM-dd")
            : null,
        attributes:
            bizGroupData?.RemovedDate?.length > 0
                ? ""
                : attributesOptions[0].value,
    };
}

function transformFormDataToBizGroupData(formData = {}, bizGroupData = {}) {
    const newBizGroupData = { ...bizGroupData };
    newBizGroupData.BizGroupId = formData.group;
    newBizGroupData.ClientId = formData.client;
    newBizGroupData.EffectiveDate = formData.effectiveDate;
    newBizGroupData.RemovedDate = formData.retireDate ?? null;

    return newBizGroupData;
}

const RenderEffectiveDate = ({ effectiveDate }) => {
    const currentEffectiveDateDate = effectiveDate
        ? getCalendarDateFromNewDate(new Date(effectiveDate))
        : null;

    return (
        <DatePicker
            defaultDateValue={currentEffectiveDateDate}
            name="effectiveDate"
            label="Effective Date"
            validations={{
                required: true,
            }}
        />
    );
};

const RenderRetireDate = ({ retiredDate }) => {
    const currentRetiredDate = retiredDate
        ? getCalendarDateFromNewDate(new Date(retiredDate))
        : null;

    const currentAttribute = useWatch({ name: "attributes" });
    const displayRetireDate = currentAttribute !== attributesOptions[0].value;

    return displayRetireDate ? (
        <DatePicker
            defaultDateValue={currentRetiredDate}
            name="retireDate"
            label="Retire Date"
            validations={{
                required: true,
            }}
        />
    ) : (
        <></>
    );
};

const ClientBizGroupsForm = ({ bizGroupIdToEdit, onClose }) => {
    const {
        clientBizGroups,
        createClientBizGroup,
        updateClientBizGroup,
        deleteClientBizGroup,
    } = useClientBizGroups();
    const { bizGroups } = useBizGroups();
    const { clientId } = useParams();
    const { clientsFilter } = useClientsFilterApi();

    if (clientBizGroups.isLoading) {
        return <div>Loading...</div>;
    }

    if (clientBizGroups.error) {
        return <div>Error</div>;
    }

    const clientOptions = clientsFilter.isLoading
        ? []
        : getDropdownOptions(clientsFilter?.data, "ClientId");

    const isEditing = bizGroupIdToEdit !== undefined;
    const modifier = isEditing ? updateClientBizGroup : createClientBizGroup;

    const currentClientBizGroupData = isEditing
        ? clientBizGroups?.data.find(
              (bizGroup) => bizGroup.BizGroupClientId === bizGroupIdToEdit
          )
        : "";

    if (isEditing && !currentClientBizGroupData) {
        return <div>Could not find contact lead</div>;
    }

    const bizGroupOptions = bizGroups.isLoading
        ? []
        : getDropdownOptions(bizGroups?.data, "BizGroupId", "BizGroupName");

    const handleOnClickDeleteBizGroup = async (prevData) => {
        const response = await deleteClientBizGroup.mutateAsync({ clientBizGroupId: bizGroupIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={
                    bizGroupOptions.find(
                        (f) =>
                            f.value ===
                            currentClientBizGroupData.BizGroupId
                    )?.label
                }
            />,
            {
                close: true,
                action: true,
                actionProps: {
                    label: "Undo",
                    onClick: () => onSubmit(prevData),
                },
            }
        );
    };

    const onSubmit = async (formData) => {
        const clientBizGroupsData = transformFormDataToBizGroupData(
            formData,
            currentClientBizGroupData
        );

        const bizGroupId = isEditing
            ? clientBizGroupsData?.BizGroupId
            : formData.group;
        const bizGroupName = bizGroupOptions.find(
            (f) => f.value === bizGroupId
        )?.label;

        const allData = {
            body: clientBizGroupsData,
        };

        const response = await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={bizGroupName}
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformBizGroupDataToFormData(
                currentClientBizGroupData,
                Number.parseInt(clientId)
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <Dropdown
                        name="client"
                        validations={{
                            required: true,
                        }}
                        label="Client"
                        options={clientOptions}
                        itemsAreLoading={clientsFilter.isLoading}
                    />
                    <Dropdown
                        name="group"
                        validations={{
                            required: true,
                        }}
                        label="Group"
                        options={bizGroupOptions}
                    />
                    <VStack>
                        <RenderEffectiveDate
                            effectiveDate={
                                currentClientBizGroupData?.EffectiveDate
                            }
                        />
                        <Options
                            name="attributes"
                            options={attributesOptions}
                        />
                        <RenderRetireDate
                            retiredDate={currentClientBizGroupData?.RemovedDate}
                        />
                    </VStack>
                </VStack>
                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteBizGroup={handleOnClickDeleteBizGroup}
                    onClose={onClose}
                    isLoading={modifier.isLoading}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    bizGroupIdToEdit: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleOnClickDeleteBizGroup: PropTypes.func
};

RenderEffectiveDate.propTypes = {
    effectiveDate: PropTypes.string
};

RenderRetireDate.propTypes = {
    retiredDate: PropTypes.string
};

ClientBizGroupsForm.propTypes = {
    bizGroupIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ClientBizGroupsForm;
